import {
  ReactNode, useContext, useEffect, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import OvTable from '../OvTable/OvTable';
import {
  ModelPortfolioTypes, OvColumn, PortfolioTypes, PortfolioReports, TriggerTypes,
} from './sharedTypes';
import { PortfolioReport } from '../../../../interfaces';
import {
  Card, CardContent, Button, MenuItem, TextField,
} from '../../../2-component';
import { UserContext } from '../../../../providers/userContextProvider';
import { FETCH_PORTFOLIO_REPORTS } from '../queriesMutations';
import {
  AvailableForCashColumn,
  ClientColumn,
  ExpectedCashColumn, FundTransferColumn,
  GoalColumn, LastUpdatedColumn,
  ModelColumn, OrganizationColumn,
  PortfolioTypeColumn, SMAIncludedColumn, TotalMarketValueColumn, TriggersColumn,
} from './rebalanceColumns';
import { Grid } from '../../../1-primative';
import { ModelPortfoliosSelect } from '../../../3-pattern';

// const TESTID_REBALANCE_REPORT_TABLE = 'rebalance-report-v2-table';
// const TESTID_REBALANCE_REPORT_TABLE_HEAD_CELL = (cell: string) => `${TESTID_REBALANCE_REPORT_TABLE}-head-cell-${cell}`;
// const TESTID_REBALANCE_REPORT_TABLE_ROW = (index: number) => `${TESTID_REBALANCE_REPORT_TABLE}-row-${index}`;
// const TESTID_REBALANCE_REPORT_TABLE_ROW_CELL = (index: number, cell: string) => `${TESTID_REBALANCE_REPORT_TABLE}-row-${index}-cell-${cell}`;
// const TESTID_REBALANCE_REPORT_TABLE_FILTER = (filter: string) => `${TESTID_REBALANCE_REPORT_TABLE}-filter-${filter}`;

const columns: OvColumn<PortfolioReport>[] = [
  {
    header: 'Client', accessor: 'client', sort: false, cell: (row): ReactNode => (<ClientColumn data={row} />),
  }, {
    header: 'Portfolio Type', accessor: 'type', sort: false, cell: (row): ReactNode => (<PortfolioTypeColumn data={row} />),
  }, {
    header: 'Goal', accessor: 'goal', sort: false, cell: (row): ReactNode => (<GoalColumn data={row} />),
  }, {
    header: 'Model', accessor: 'portfolio', sort: false, cell: (row): ReactNode => (<ModelColumn data={row} />),
  }, {
    header: 'Available Cash For Trade', accessor: 'cashAvailableForTradeCents', sort: true, cell: (row): ReactNode => (<AvailableForCashColumn data={row} />),
  }, {
    header: 'Expected Cash', accessor: 'expectedCashCents', sort: true, cell: (row): ReactNode => (<ExpectedCashColumn data={row} />),
  }, {
    header: 'Total Market Value', accessor: 'totalMarketValue', sort: true, cell: (row): ReactNode => (<TotalMarketValueColumn data={row} />),
  }, {
    header: 'Triggers', accessor: 'triggers', sort: false, cell: (row): ReactNode => (<TriggersColumn data={row} />),
  }, {
    header: 'SMA included', accessor: 'smaIncluded', sort: false, cell: (row): ReactNode => (<SMAIncludedColumn data={row} />),
  }, {
    header: 'Fund Transfer', accessor: 'fundTransfer', sort: false, cell: (row): ReactNode => (<FundTransferColumn data={row} />),
  }, {
    header: 'Organization', accessor: 'organization', sort: false, cell: (row): ReactNode => (<OrganizationColumn data={row} />),
  }, {
    header: 'Last Updated', accessor: 'updatedAt', sort: true, cell: (row): ReactNode => (<LastUpdatedColumn data={row} />),
  },
];

const RebalancePage = () => {
  const navigate = useNavigate();

  const [data, setData] = useState<PortfolioReport[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(15);
  const [sortColumn, setSortColumn] = useState<keyof PortfolioReport | null>('createdAt');
  const [sorDirection, setSorDirection] = useState<'asc' | 'desc' | undefined>('desc');
  const [totalItems, setTotalItems] = useState<number>(0);
  const [selected, setSelected] = useState<PortfolioReport[]>([]);

  const [triggerType, setTriggerType] = useState<number>(TriggerTypes.ANY);
  const [portifolioType, setPortifolioType] = useState<keyof typeof PortfolioTypes>(PortfolioTypes.ANY);
  const [model, setModel] = useState<string>(ModelPortfolioTypes.ANY);

  const { activeOrganization } = useContext(UserContext);

  const { t } = useTranslation(['rebalanceReport']);
  const { loading, refetch } = useQuery(FETCH_PORTFOLIO_REPORTS, {
    fetchPolicy: 'no-cache',
    variables: {
      input: {
        filter: {
          state: 'ACTIVE',
          organizationId: activeOrganization.id,
          anyRebalanceRequired: triggerType === TriggerTypes.BOTH ? true : undefined,
          cashTrigger: triggerType === TriggerTypes.CASH ? true : undefined,
          driftRebalanceRequired: triggerType === TriggerTypes.DRIFT ? true : undefined,
          modelPortfolioType: portifolioType !== PortfolioTypes.ANY ? portifolioType : undefined,
          modelPortfolioId: model !== ModelPortfolioTypes.ANY ? model : undefined,
        },
        pagination: {
          sortField: sortColumn,
          sortDesc: sorDirection === 'desc',
          perPage: itemsPerPage,
          offSet: (currentPage - 1) * itemsPerPage,
        },
      },
    },
    onCompleted: (response: PortfolioReports<PortfolioReport>) => {
      setData(response.fetchPortfolioReports.portfolioReports || []);
      setTotalItems(response.fetchPortfolioReports.totalCount);
    },
  });

  const listenPage = (page: number, rowsPerPage: number): void => {
    setItemsPerPage(rowsPerPage);
    setCurrentPage(page);
  };

  const listenSort = (column: keyof PortfolioReport | null, direction: 'asc' | 'desc' | undefined): void => {
    setSortColumn(column);
    setSorDirection(direction);
  };

  const listenSelected = (tableData: PortfolioReport[]): void => {
    setSelected(tableData);
  };

  const onRowClick = (row: PortfolioReport): void => {
    navigate(`/rebalanceReportV2/${row.object.id}`);
  };

  useEffect(() => {
    refetch();
  }, [currentPage, itemsPerPage, sortColumn, sorDirection, triggerType, portifolioType, model, refetch]);

  return (
    <Card loading={loading}>
      <CardContent>
        <Grid container gap={1} justifyContent='space-between' alignItems='center'>
          <Grid item xs={6}>
            <Grid container gap={1} flexWrap='nowrap'>
              <Grid item xs={4}>
                <TextField
                  select
                  fullWidth
                  label={t('filters.triggerType')}
                  value={triggerType}
                  onChange={(e: any) => setTriggerType(e.target.value)}>
                  <MenuItem value={TriggerTypes.BOTH}>{t('filters.anyRebalanceRequired')}</MenuItem>
                  <MenuItem value={TriggerTypes.DRIFT}>{t('filters.driftRebalance')}</MenuItem>
                  <MenuItem value={TriggerTypes.CASH}>{t('filters.cash')}</MenuItem>
                  <MenuItem value={TriggerTypes.ANY}>{t('any')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  select
                  fullWidth
                  label={t('filters.modalportfolioType')}
                  value={portifolioType}
                  onChange={(e: any) => setPortifolioType(e.target.value)}>
                  <MenuItem value={PortfolioTypes.ANY}>{t('any')}</MenuItem>
                  <MenuItem value={PortfolioTypes.GOAL}>{t('goal')}</MenuItem>
                  <MenuItem value={PortfolioTypes.SUB_ACCOUNT}>{t('subAccount')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={4}>
                <ModelPortfoliosSelect
                  label={t('filters.selectPortfolio')}
                  value={model}
                  onChange={(e: any) => setModel(e.target.value)}
                  includeAnyOption />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs='auto'>
            <Button
              label={'Auto Rebalance'}
              variant='filled'
              disabled={!selected.length}
            />
          </Grid>
        </Grid>
      </CardContent>
      <OvTable<PortfolioReport>
        data={data} columns={columns} loading={loading} checkbox={true} pagination={true}
        linesPerPage={itemsPerPage} initialPage={currentPage} totalLines={totalItems}
        onPage={listenPage} onSort={listenSort} onSelect={listenSelected} onTableRowClick={onRowClick} />
    </Card>
  );
};

export default RebalancePage;
